import * as React from "react";

const HighlightedCodeBody = React.lazy(
  () => import("@/shared/components/HighlightedCode/HighlightedCodeBody"),
);

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  type AgentThreadToolResultContent,
  type AgentThreadToolUseContent,
} from "@/shared/domain/agentThread";
import { useToolDetails } from "@/shared/domain/agentThread/hooks";
import { withoutServerInjectedFields } from "@/shared/domain/agentThread/toolInput";
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
} from "@/shared/foundation/icons/mui";

import { AnalyzeTopicOutput } from "./AnalyzeTopicOutput";
import { detectAnalyzeTopicPayload } from "./analyzeTopicPayload";
import { CodeBlock } from "./CodeBlock";
import { CopyButton, JsonSection } from "./JsonSection";
import { RunPythonCard } from "./RunPythonCard";
import {
  detectRunPythonPayload,
  extractRunPythonCode,
} from "./runPythonPayload";
import styles from "./ToolDetailPanel.module.css";

const MS_TO_SECONDS_THRESHOLD = 100;

function formatRuntime(runtimeMs: number): string {
  if (runtimeMs < MS_TO_SECONDS_THRESHOLD) {
    return `${runtimeMs}ms`;
  }

  return `${(runtimeMs / 1000).toFixed(2)}s`;
}

/**
 * If raw_request has an `input` key (Anthropic tool_use format),
 * return just the input. Otherwise return as-is.
 */
function extractInput(
  rawRequest: Record<string, unknown> | undefined,
): unknown {
  if (!rawRequest) {
    return null;
  }

  if ("input" in rawRequest) {
    return rawRequest.input;
  }

  return rawRequest;
}

const SQL_FIELD_NAMES = ["sql_query", "sql"] as const;

interface CodeField {
  code: string;
  fieldName: string;
  language: string;
}

/**
 * The input field holding source code, rendered highlighted. `code` is only claimed for
 * `run_python`: other tools' `code` fields need not be Python.
 */
function extractCodeField(toolName: string, input: unknown): CodeField | null {
  if (toolName === "run_python") {
    const code = extractRunPythonCode(input);
    return code === null
      ? null
      : { code, fieldName: "code", language: "python" };
  }
  if (!isPlainObject(input)) {
    return null;
  }
  for (const key of SQL_FIELD_NAMES) {
    const v = input[key];
    if (typeof v === "string" && v.trim().length > 0) {
      return { code: v, fieldName: key, language: "sql" };
    }
  }
  return null;
}

function omitKey(input: unknown, key: string): unknown {
  if (!isPlainObject(input)) {
    return input;
  }
  const copy = { ...input };
  delete copy[key];
  return copy;
}

/**
 * Unwrap the Anthropic toolResult > content > text pattern if present.
 * Attempts to parse JSON-encoded text for pretty display.
 * Falls back to raw_response as-is.
 */
function extractOutput(
  rawResponse: Record<string, unknown> | undefined,
): unknown {
  if (!rawResponse) {
    return null;
  }

  const toolResult = rawResponse.toolResult;
  if (
    typeof toolResult === "object" &&
    toolResult !== null &&
    "content" in toolResult
  ) {
    const result = toolResult as Record<string, unknown>;
    const content = result.content;

    if (Array.isArray(content)) {
      const textParts: unknown[] = [];

      for (const block of content) {
        if (typeof block === "object" && block !== null && "text" in block) {
          const textValue = (block as Record<string, unknown>).text;

          if (typeof textValue === "string") {
            try {
              textParts.push(JSON.parse(textValue));
            } catch {
              textParts.push(textValue);
            }
          } else {
            textParts.push(textValue);
          }
        } else {
          textParts.push(block);
        }
      }

      if (textParts.length === 1) {
        return textParts[0];
      }

      if (textParts.length > 1) {
        return textParts;
      }
    }
  }

  return rawResponse;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

interface ToolCallInputProps {
  toolName: string;
  /** The call's input, without the server's meta-fields. */
  input: unknown;
}

/**
 * A tool call's arguments: a source-code argument highlighted, the rest as a
 * JSON tree. Shared by the detail panel and the live call row.
 */
export function ToolCallInput({
  toolName,
  input,
}: ToolCallInputProps): React.JSX.Element {
  const codeField = extractCodeField(toolName, input);
  const inputForJson = codeField ? omitKey(input, codeField.fieldName) : input;
  const showInputJson =
    !codeField ||
    (isPlainObject(inputForJson) && Object.keys(inputForJson).length > 0);

  return (
    <>
      {codeField && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <RobotoTypography component="p" className={styles.sectionTitle}>
              {codeField.fieldName}
            </RobotoTypography>
          </div>
          <CodeBlock
            className={styles.codeField}
            code={codeField.code}
            language={codeField.language}
            highlightedChildren={
              <React.Suspense fallback={<code>{codeField.code}</code>}>
                <HighlightedCodeBody
                  code={codeField.code}
                  language={codeField.language}
                />
              </React.Suspense>
            }
          />
        </div>
      )}
      {showInputJson && (
        <JsonSection
          label={codeField ? "Other input" : "Input"}
          value={inputForJson}
        />
      )}
    </>
  );
}

interface ToolDetailPanelContentProps {
  toolResult: AgentThreadToolResultContent;
  toolUse: AgentThreadToolUseContent;
  /** Lets a `run_python` card offer its files for download. */
  threadId?: string;
}

export function ToolDetailPanelContent({
  toolResult,
  toolUse,
  threadId,
}: ToolDetailPanelContentProps): React.JSX.Element {
  const isSuccess = toolResult.status === "success";

  const rawInput = toolUse.input ?? extractInput(toolUse.raw_request);
  // `raw_request` is whatever the provider echoed back, so it is not
  // guaranteed to be an object the way `input` is.
  const input = isPlainObject(rawInput)
    ? withoutServerInjectedFields(rawInput)
    : rawInput;
  const output = extractOutput(toolResult.raw_response);

  // analyze_topic emits a structured TOON payload (and historically a
  // ``{topic_ids, data}`` JSON object). Both shapes get the table renderer;
  // anything we don't recognize falls through to the generic JSON view below.
  const analyzeTopicPayload =
    toolResult.tool_name === "analyze_topic"
      ? detectAnalyzeTopicPayload(output)
      : null;
  const useAnalyzeTopicRenderer =
    analyzeTopicPayload !== null && analyzeTopicPayload.kind !== "unknown";
  // run_python gets its own card; a non-payload result shows as a sandbox error.
  if (toolResult.tool_name === "run_python") {
    return (
      <RunPythonCard
        toolUseId={toolResult.tool_use_id}
        input={input}
        threadId={threadId}
        state={{
          kind: "finished",
          payload:
            detectRunPythonPayload(output) ??
            detectRunPythonPayload(toolResult.payload),
          toolSucceeded: toolResult.status === "success",
          runtimeMs: toolResult.runtime_ms,
          rawOutput: output,
        }}
      />
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <RobotoTypography component="p" className={styles.toolName}>
            {toolResult.tool_name}
          </RobotoTypography>
          <RobotoTypography component="p" className={styles.toolUseId}>
            {toolResult.tool_use_id}
          </RobotoTypography>
        </div>
        <div className={styles.headerRight}>
          <span
            className={
              isSuccess ? styles.statusBadgeSuccess : styles.statusBadgeError
            }
          >
            {isSuccess ? (
              <CheckCircleOutlineIcon className={styles.statusIcon} />
            ) : (
              <ErrorOutlineIcon className={styles.statusIcon} />
            )}
            {isSuccess ? "Success" : "Failed"}
          </span>
          <RobotoTypography component="span" className={styles.runtime}>
            {formatRuntime(toolResult.runtime_ms)}
          </RobotoTypography>
        </div>
      </div>

      <ToolCallInput toolName={toolResult.tool_name} input={input} />
      {useAnalyzeTopicRenderer && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <RobotoTypography component="p" className={styles.sectionTitle}>
              Output
            </RobotoTypography>
            <CopyButton value={output} />
          </div>
          <AnalyzeTopicOutput output={output} />
        </div>
      )}
      {!useAnalyzeTopicRenderer && (
        <JsonSection label="Output" value={output} />
      )}
    </div>
  );
}

interface ToolDetailPanelProps {
  threadId: string;
  toolUseId: string;
}

export function ToolDetailPanel({
  threadId,
  toolUseId,
}: ToolDetailPanelProps): React.JSX.Element | null {
  const { data, error, isFetching, isPending, refetch } = useToolDetails(
    threadId,
    toolUseId,
  );

  React.useEffect(() => {
    void refetch();
  }, [threadId, refetch, toolUseId]);

  if ((isPending || isFetching) && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.loadingText}>
          Loading tool details…
        </RobotoTypography>
      </div>
    );
  }

  if (error && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.toolName}>
          Failed to load tool details
        </RobotoTypography>
        <RobotoTypography component="p" className={styles.errorText}>
          {error.message}
        </RobotoTypography>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <ToolDetailPanelContent
      toolResult={data.tool_result}
      toolUse={data.tool_use}
      threadId={threadId}
    />
  );
}
